import React, { useState } from 'react';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Copy,
  MessageSquare,
  Hash,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { useListQuickRepliesQuery, QuickReplyItem } from '@/client/api/whatsapp/list-quick-replies.query';
import { useCreateQuickReplyMutation } from '@/client/api/whatsapp/create-quick-reply.mutation';
import { useUpdateQuickReplyMutation } from '@/client/api/whatsapp/update-quick-reply.mutation';
import { useDeleteQuickReplyMutation } from '@/client/api/whatsapp/delete-quick-reply.mutation';
import ConfirmDeleteDialog from '@/client/components/admin/WhatsApp/ConfirmDeleteDialog';

const QuickRepliesPage: React.FC = () => {
  const { data, isLoading, refetch } = useListQuickRepliesQuery();
  const replies = data?.data?.quick_replies || [];
  const createMutation = useCreateQuickReplyMutation();
  const updateMutation = useUpdateQuickReplyMutation();
  const deleteMutation = useDeleteQuickReplyMutation();

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<QuickReplyItem | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formBody, setFormBody] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<QuickReplyItem | null>(null);

  const categories = Array.from(new Set(replies.map(r => r.category).filter(Boolean))) as string[];

  const filtered = replies.filter(r => {
    const matchesSearch =
      r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.body.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || r.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleCopy = (id: string, message: string) => {
    navigator.clipboard.writeText(message);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const openCreate = () => {
    setEditing(null);
    setFormTitle('');
    setFormBody('');
    setFormCategory('');
    setShowModal(true);
  };

  const openEdit = (reply: QuickReplyItem) => {
    setEditing(reply);
    setFormTitle(reply.title);
    setFormBody(reply.body);
    setFormCategory(reply.category || '');
    setShowModal(true);
  };

  const handleSave = () => {
    if (!formTitle.trim() || !formBody.trim()) return;
    const payload = { title: formTitle, body: formBody, category: formCategory || undefined };

    if (editing) {
      updateMutation.mutate(
        { reply_id: editing.reply_id, ...payload },
        {
          onSuccess: () => { toast.success('Quick reply updated'); setShowModal(false); refetch(); },
          onError: (error) => toast.error(error.message || 'Failed to update the quick reply'),
        }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => { toast.success('Quick reply created'); setShowModal(false); refetch(); },
        onError: (error) => toast.error(error.message || 'Failed to create the quick reply'),
      });
    }
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(
      { reply_id: deleteTarget.reply_id },
      {
        onSuccess: () => { toast.success('Quick reply deleted'); setDeleteTarget(null); refetch(); },
      }
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quick Replies</h1>
            <p className="text-sm text-gray-500 mt-1">Manage shortcut messages for faster customer responses</p>
          </div>
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Quick Reply
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Total Quick Replies</span>
              <MessageSquare className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{replies.length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Categories</span>
              <Hash className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
          </div>
        </div>

        {/* Filter & Search */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by title or message..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          {isLoading && <p className="p-4 text-sm text-gray-500">Loading...</p>}

          <div className="divide-y divide-gray-200">
            {filtered.map(reply => (
              <div key={reply.reply_id} className="p-5 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-semibold">
                        {reply.title}
                      </span>
                      {reply.category && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                          {reply.category}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700">{reply.body}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleCopy(reply.reply_id, reply.body)}
                      className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                      title="Copy message"
                    >
                      {copiedId === reply.reply_id
                        ? <span className="text-xs text-green-600 font-medium">Copied!</span>
                        : <Copy className="w-4 h-4 text-gray-600" />}
                    </button>
                    <button onClick={() => openEdit(reply)} className="p-1.5 hover:bg-gray-100 rounded transition-colors">
                      <Edit className="w-4 h-4 text-gray-600" />
                    </button>
                    <button onClick={() => setDeleteTarget(reply)} className="p-1.5 hover:bg-gray-100 rounded transition-colors">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {!isLoading && filtered.length === 0 && (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No quick replies found</h3>
              <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">{editing ? 'Edit' : 'Add'} Quick Reply</h2>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category (optional)</label>
                <input
                  type="text"
                  value={formCategory}
                  onChange={(e) => setFormCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={formBody}
                  onChange={(e) => setFormBody(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={createMutation.isPending || updateMutation.isPending || !formTitle.trim() || !formBody.trim()}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Quick Reply"
        itemLabel={deleteTarget?.title || 'this quick reply'}
        onConfirm={confirmDelete}
        isPending={deleteMutation.isPending}
        errorMessage={deleteMutation.error?.message}
      />
    </div>
  );
};

export default QuickRepliesPage;
